import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import {
  CellContextMenuEvent,
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FilterOpenedEvent,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { debounce } from 'lodash'
import { openDecisionPage } from 'utils/navigationHelper'
import { loadTableState, saveTableState } from 'utils/tableStateStorage'
import {
  isApprovalsRowContextMenuVar,
  isAssignmentsColumnHeaderRightClickMenuVar,
  showCustomizeColumnsVar,
} from 'cache/adv-filtering'
import { selectedApprovalRowsVar } from 'cache/approvals'
import { PendingApprovalsState } from 'cache/pending-approvals'
import { AgContextMenuContainer, AgRowContextMenuContainer, defaultColDef } from 'components/AgGrid'
import { handleCloseCustomizeColumns } from 'components/AgGrid/components/AgContextMenu/helperFns'
import CustomizeColumns from 'components/AgGrid/components/Modals/CustomizeColumns'
import Loader from 'components/Loader/Loader'
import { ApprovalDeal } from 'ts/interfaces/approvalDeal.interface'
import { BaseTableColumn } from 'ts/types/commonTable.types'
import { approvalsTableOptions, frameworkComponents } from './agGridConfig/agGridConfig'
import { approvalColDef } from './agGridConfig/columnDefs'
import styles from './styles.module.scss'

type ApprovalsTableProps = {
  pendingApprovals: PendingApprovalsState
  loading: boolean
  lastManualRefreshAt: number | null
}

const TABLE_STATE_KEY = 'approvalsTable'

export const LOCALE_TEXT = { noRowsToShow: 'No deals found' } as const

export const ApprovalsTable = ({
  pendingApprovals,
  loading,
  lastManualRefreshAt,
}: ApprovalsTableProps) => {
  const approvalsTableRef = useRef<AgGridReact<ApprovalDeal>>(null)
  const [gridApi, setGridApi] = useState<GridApi<ApprovalDeal> | null>(null)
  const showCustomizeColumns = useReactiveVar(showCustomizeColumnsVar)
  const [isStateRestored, setIsStateRestored] = useState(false)

  const columnState = useMemo(() => {
    return showCustomizeColumns && gridApi ? gridApi.getColumnState() || [] : []
  }, [showCustomizeColumns, gridApi])

  // Save table state to localStorage whenever it changes
  const saveCurrentTableState = useCallback(() => {
    if (!isStateRestored || !gridApi) return
    try {
      const columnState = gridApi.getColumnState()
      const filterModel = gridApi.getFilterModel()
      saveTableState(TABLE_STATE_KEY, {
        columnState,
        filterModel,
      })
    } catch (error) {
      console.error('Error saving table state:', error)
    }
  }, [gridApi, isStateRestored])

  const debouncedSaveTableState = useMemo(
    () => debounce(() => saveCurrentTableState(), 500),
    [saveCurrentTableState]
  )

  useEffect(() => {
    return () => {
      debouncedSaveTableState.cancel()
    }
  }, [debouncedSaveTableState])

  const onGridReady = useCallback((params: GridReadyEvent<ApprovalDeal>) => {
    const api = params.api
    setGridApi(api)

    // Restore saved table state
    const savedState = loadTableState(TABLE_STATE_KEY)
    if (savedState && api) {
      const currentColIds = new Set(approvalColDef.map((col) => col.field))

      if (savedState.columnState) {
        const validColumnState = savedState.columnState.filter((col) =>
          currentColIds.has(col.colId)
        )
        if (validColumnState.length > 0) {
          api.applyColumnState({ state: validColumnState, applyOrder: true })
        }
      }
      if (savedState.filterModel) {
        api.setFilterModel(savedState.filterModel)
      }
    }
    setIsStateRestored(true)
  }, [])

  const approvalColumnsForCustomize: BaseTableColumn[] = useMemo(
    () =>
      approvalColDef.map((col) => {
        const colId =
          typeof col.colId === 'string' ? col.colId : typeof col.field === 'string' ? col.field : ''

        return {
          colId,
          key: colId,
          label: col.headerName ?? colId,
          userProvidedColDef: {
            headerName: col.headerName,
          },
        }
      }),
    []
  )

  const onSelectionChanged = (event: SelectionChangedEvent<ApprovalDeal>) => {
    const selected = event.api.getSelectedRows()
    selectedApprovalRowsVar(selected.length > 0 ? selected : undefined)
  }
  const onRowDoubleClicked = (event: RowDoubleClickedEvent<ApprovalDeal>) => {
    if (event.data?.dealNr && event.data?.decisionPagePath?.trim()) {
      openDecisionPage(event.data)
    }
  }

  const handleTableState = useCallback(
    (_event: ColumnMovedEvent | ColumnVisibleEvent | SortChangedEvent | FilterChangedEvent) => {
      saveCurrentTableState()
    },
    [saveCurrentTableState]
  )

  const containerClassName = useMemo(
    () => clsx('ag-theme-alpine', styles.approvalsTableContainer),
    []
  )

  const onColumnResized = useCallback(
    (_event: ColumnResizedEvent) => {
      debouncedSaveTableState()
    },
    [debouncedSaveTableState]
  )

  const handleCustomizeColumnsClose = useCallback(
    (hiddenColumns?: string[], visibleColumns?: string[]) => {
      if (gridApi) {
        handleCloseCustomizeColumns(gridApi, hiddenColumns, visibleColumns)
        // Save state after customizing columns
        setTimeout(() => {
          saveCurrentTableState()
        }, 0)
      }
    },
    [gridApi, saveCurrentTableState]
  )

  // Dependencies are intentionally empty because:
  // 1. isApprovalsRowContextMenuVar is a reactive variable (not React state)
  // 2. params comes from the callback parameter, not closure
  // 3. No external values are captured that could become stale
  const handleCellContextMenu = useCallback((params: CellContextMenuEvent<ApprovalDeal>) => {
    try {
      if (params.event && params.event instanceof MouseEvent) {
        params.event.preventDefault()
        isApprovalsRowContextMenuVar({
          mouseEvent: params.event,
          params,
        })
      }
    } catch (error) {
      console.error('Error opening context menu:', error)
    }
  }, [])

  const approvalsDefaultColDef = useMemo<ColDef<ApprovalDeal>>(
    () => ({
      ...(defaultColDef as ColDef<ApprovalDeal>),
      headerComponentParams: {
        ...(defaultColDef.headerComponentParams || {}),
        enableFilter: true,
      },
    }),
    []
  )

  const getRowClass = useCallback((params: RowClassParams<ApprovalDeal>) => {
    const rowData = params.data
    if (!rowData) {
      return undefined
    }

    if (rowData.highRisk === 'Y' && rowData.highRiskDealEUVNotCompletedYet === 'Y') {
      return 'background-pink'
    }

    return undefined
  }, [])

  const onFilterOpened = useCallback((params: FilterOpenedEvent) => {
    try {
      const column = params.column
      if (!column || !('getColId' in column)) {
        return
      }
      const colDef = 'getColDef' in column ? column.getColDef() : undefined
      if (!colDef || colDef.filter === 'customDateFilter' || typeof colDef.filter !== 'string') {
        // Skip custom filters - they manage their own state
        return
      }
      const popup = document.querySelector<HTMLElement>('.ag-multi-filter')
      if (!popup) {
        return
      }

      const colId = column.getColId()
      if (!colId) {
        return
      }
      const applyButtons = popup.querySelectorAll<HTMLButtonElement>(
        '.ag-filter-apply-panel-button'
      )
      applyButtons.forEach((applyButton) => {
        if (applyButton.dataset.filterApplyHandlerAttached === 'true') {
          return
        }
        applyButton.dataset.filterApplyHandlerAttached = 'true'
        applyButton.addEventListener('click', () => {
          try {
            const currentModel = params.api.getFilterModel() as Record<string, unknown> | null
            const newModel: Record<string, unknown> = { ...(currentModel ?? {}) }
            delete newModel[colId]
            params.api.setFilterModel(newModel)
          } catch (error) {
            console.error('Error handling filter apply button click:', error)
          }
        })
      })
    } catch (error) {
      console.error('Error configuring filter popup footer:', error)
    }
  }, [])

  useEffect(() => {
    if (!gridApi || !isStateRestored || lastManualRefreshAt === null) {
      return
    }

    try {
      gridApi.ensureIndexVisible(0)
      saveCurrentTableState()
    } catch (error) {
      console.error('Error applying refresh behavior on approvals table:', error)
    }
  }, [gridApi, isStateRestored, lastManualRefreshAt, saveCurrentTableState])

  return (
    <section className={containerClassName} aria-label="Approvals table">
      <Loader open={loading} />
      <AgGridReact
        ref={approvalsTableRef}
        rowData={pendingApprovals.deals}
        columnDefs={approvalColDef}
        defaultColDef={approvalsDefaultColDef}
        getRowClass={getRowClass}
        onGridReady={onGridReady}
        suppressNoRowsOverlay={loading}
        localeText={LOCALE_TEXT}
        onSelectionChanged={onSelectionChanged}
        onRowDoubleClicked={onRowDoubleClicked}
        components={frameworkComponents}
        onCellContextMenu={handleCellContextMenu}
        onColumnMoved={handleTableState}
        onColumnResized={onColumnResized}
        onColumnVisible={handleTableState}
        onSortChanged={handleTableState}
        onFilterChanged={handleTableState}
        onFilterOpened={onFilterOpened}
        {...approvalsTableOptions}
      />
      <AgContextMenuContainer
        gridApi={gridApi ?? undefined}
        columnHeaderRightClickMenuVar={isAssignmentsColumnHeaderRightClickMenuVar}
        columnDefs={approvalColDef}
        tableKey={TABLE_STATE_KEY}
      />
      <AgRowContextMenuContainer rowContextMenuVar={isApprovalsRowContextMenuVar} />
      {showCustomizeColumns && gridApi && (
        <CustomizeColumns
          columns={columnState}
          columnDefs={approvalColumnsForCustomize}
          onClose={handleCustomizeColumnsClose}
        />
      )}
    </section>
  )
}
export default ApprovalsTable
